import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DollarSign,
  Calculator,
  TrendingUp,
  Award,
  Check,
  Calendar,
  Zap,
  ArrowRight,
  Layers
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { TIERS, getTierColorClass, formatReferralRange, getTierByReferrals } from "@/config/tiers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Plan pricing configuration
const PLAN_PRICES = {
  basic: { name: "Basic", price: 29.99 },
  premium: { name: "Premium", price: 59.99 },
  brokerage: { name: "Brokerage", price: 199.00 },
};

const Payments = () => {
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  
  // Calculator mode
  const [calculatorMode, setCalculatorMode] = useState("simple");
  
  // Simple calculator inputs
  const [simpleReferrals, setSimpleReferrals] = useState(30);
  const [simplePlanType, setSimplePlanType] = useState("premium");
  const [customPrice, setCustomPrice] = useState("");
  const [simpleTimeframe, setSimpleTimeframe] = useState("monthly");
  
  // Advanced calculator inputs
  const [basicReferrals, setBasicReferrals] = useState(15);
  const [premiumReferrals, setPremiumReferrals] = useState(10);
  const [brokerageReferrals, setBrokerageReferrals] = useState(5);

  useEffect(() => {
    document.title = "Payments & Calculator | GeniusList Partners";
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API}/payments/history?limit=10`, {
        withCredentials: true
      });
      setPayments(response.data);
    } catch (error) {
      console.error("Payments fetch error:", error);
      toast.error("Failed to load payment history");
    } finally {
      setLoading(false);
    }
  };

  // Get effective price for simple calculator
  const getSimplePrice = () => {
    if (simplePlanType === "custom" && customPrice) {
      return parseFloat(customPrice) || 0;
    }
    return PLAN_PRICES[simplePlanType]?.price || 59.99;
  };

  // Simple calculator computation (local)
  const simpleCalculation = useMemo(() => {
    // Inline price calculation to avoid dependency issues
    const price = simplePlanType === "custom" && customPrice 
      ? (parseFloat(customPrice) || 0) 
      : (PLAN_PRICES[simplePlanType]?.price || 59.99);
    
    const tier = getTierByReferrals(simpleReferrals);
    const commissionRate = tier.commissionRate / 100;
    
    const monthlyGross = simpleReferrals * price;
    const monthlyCommission = monthlyGross * commissionRate;
    
    let displayCommission = monthlyCommission;
    let displayGross = monthlyGross;
    
    if (simpleTimeframe === "quarterly") {
      displayCommission = monthlyCommission * 3;
      displayGross = monthlyGross * 3;
    } else if (simpleTimeframe === "annually") {
      displayCommission = monthlyCommission * 12;
      displayGross = monthlyGross * 12;
    }
    
    const annualProjection = monthlyCommission * 12;
    const twoYearLtv = annualProjection * 2 * 0.85; // 85% retention factor
    
    // Calculate tier comparison
    const tierComparison = TIERS.map((t) => ({
      tier: t.id,
      name: t.name,
      commission_rate: t.commissionRate,
      monthly_earnings: Math.round(monthlyGross * (t.commissionRate / 100) * 100) / 100,
    }));
    
    return {
      monthly_earnings: Math.round(displayCommission * 100) / 100,
      gross_revenue: Math.round(displayGross * 100) / 100,
      your_commission: Math.round(displayCommission * 100) / 100,
      annual_projection: Math.round(annualProjection * 100) / 100,
      two_year_ltv: Math.round(twoYearLtv * 100) / 100,
      tier: tier.id,
      commission_rate: tier.commissionRate,
      tier_comparison: tierComparison,
    };
  }, [simpleReferrals, simplePlanType, customPrice, simpleTimeframe]);

  // Advanced calculator computation (local)
  const advancedCalculation = useMemo(() => {
    const totalReferrals = basicReferrals + premiumReferrals + brokerageReferrals;
    const tier = getTierByReferrals(totalReferrals);
    const commissionRate = tier.commissionRate / 100;
    const tierIndex = TIERS.findIndex((t) => t.id === tier.id);
    const nextTier = tierIndex < TIERS.length - 1 ? TIERS[tierIndex + 1] : null;
    
    // Calculate per-plan breakdown
    const basicRevenue = basicReferrals * PLAN_PRICES.basic.price;
    const premiumRevenue = premiumReferrals * PLAN_PRICES.premium.price;
    const brokerageRevenue = brokerageReferrals * PLAN_PRICES.brokerage.price;
    
    const basicCommission = basicRevenue * commissionRate;
    const premiumCommission = premiumRevenue * commissionRate;
    const brokerageCommission = brokerageRevenue * commissionRate;
    
    const totalMonthlyRevenue = basicRevenue + premiumRevenue + brokerageRevenue;
    const totalMonthlyCommission = basicCommission + premiumCommission + brokerageCommission;
    const annualProjection = totalMonthlyCommission * 12;
    
    // Calculate incremental commission if advanced to next tier
    let incrementalCommission = 0;
    let nextTierCommission = 0;
    if (nextTier) {
      const nextTierRate = nextTier.commissionRate / 100;
      nextTierCommission = totalMonthlyRevenue * nextTierRate;
      incrementalCommission = nextTierCommission - totalMonthlyCommission;
    }
    
    // Tier comparison with current inputs
    const tierComparison = TIERS.map((t) => ({
      tier: t.id,
      name: t.name,
      commission_rate: t.commissionRate,
      monthly_earnings: Math.round(totalMonthlyRevenue * (t.commissionRate / 100) * 100) / 100,
    }));
    
    return {
      totalReferrals,
      tier,
      commissionRate: tier.commissionRate,
      breakdown: {
        basic: {
          referrals: basicReferrals,
          revenue: Math.round(basicRevenue * 100) / 100,
          commission: Math.round(basicCommission * 100) / 100,
        },
        premium: {
          referrals: premiumReferrals,
          revenue: Math.round(premiumRevenue * 100) / 100,
          commission: Math.round(premiumCommission * 100) / 100,
        },
        brokerage: {
          referrals: brokerageReferrals,
          revenue: Math.round(brokerageRevenue * 100) / 100,
          commission: Math.round(brokerageCommission * 100) / 100,
        },
      },
      totalMonthlyRevenue: Math.round(totalMonthlyRevenue * 100) / 100,
      totalMonthlyCommission: Math.round(totalMonthlyCommission * 100) / 100,
      annualProjection: Math.round(annualProjection * 100) / 100,
      nextTier,
      nextTierCommission: Math.round(nextTierCommission * 100) / 100,
      incrementalCommission: Math.round(incrementalCommission * 100) / 100,
      tierComparison,
    };
  }, [basicReferrals, premiumReferrals, brokerageReferrals]);

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split("-");
    const date = new Date(year, month - 1);
    return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="bg-white border-slate-100">
            <CardContent className="p-6">
              <Skeleton className="h-6 w-40 mb-6" />
              <Skeleton className="h-8 w-full mb-4" />
              <Skeleton className="h-8 w-full mb-4" />
              <Skeleton className="h-8 w-full" />
            </CardContent>
          </Card>
          <Card className="bg-white border-slate-100">
            <CardContent className="p-6">
              <Skeleton className="h-6 w-40 mb-6" />
              <Skeleton className="h-24 w-full" />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Calculator Section */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Calculator Inputs */}
        <Card className="bg-white border-slate-100 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Calculator className="w-5 h-5 text-purple-600" />
              Commission Calculator
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Calculator Mode Tabs */}
            <Tabs value={calculatorMode} onValueChange={setCalculatorMode} className="w-full">
              <TabsList className="grid grid-cols-2 w-full">
                <TabsTrigger value="simple" data-testid="calc-mode-simple" className="flex items-center gap-2">
                  <Calculator className="w-4 h-4" />
                  Simple
                </TabsTrigger>
                <TabsTrigger value="advanced" data-testid="calc-mode-advanced" className="flex items-center gap-2">
                  <Layers className="w-4 h-4" />
                  Advanced
                </TabsTrigger>
              </TabsList>

              {/* Simple Mode */}
              <TabsContent value="simple" className="space-y-5 mt-4">
                {/* Number of Referrals */}
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-sm font-medium text-slate-700">
                      Number of Referrals
                    </label>
                    <span className="text-sm font-bold text-purple-600">{simpleReferrals}</span>
                  </div>
                  <Slider
                    value={[simpleReferrals]}
                    onValueChange={(val) => setSimpleReferrals(val[0])}
                    min={0}
                    max={100}
                    step={1}
                    className="w-full"
                    data-testid="simple-referrals-slider"
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>0</span>
                    <span>100</span>
                  </div>
                </div>

                {/* Plan Price Selection */}
                <div>
                  <div className="flex justify-between mb-2">
                    <label className="text-sm font-medium text-slate-700">
                      Average Plan Price
                    </label>
                    <span className="text-sm font-bold text-purple-600">
                      ${getSimplePrice().toFixed(2)}
                    </span>
                  </div>
                  <Select value={simplePlanType} onValueChange={setSimplePlanType}>
                    <SelectTrigger data-testid="simple-plan-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="basic">Basic - $29.99/month</SelectItem>
                      <SelectItem value="premium">Premium - $59.99/month</SelectItem>
                      <SelectItem value="brokerage">Brokerage - $199.00/month</SelectItem>
                      <SelectItem value="custom">Custom Price</SelectItem>
                    </SelectContent>
                  </Select>
                  {simplePlanType === "custom" && (
                    <div className="mt-2">
                      <Input
                        type="number"
                        placeholder="Enter custom price"
                        value={customPrice}
                        onChange={(e) => setCustomPrice(e.target.value)}
                        className="w-full"
                        data-testid="custom-price-input"
                      />
                    </div>
                  )}
                </div>

                {/* Timeframe */}
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-2 block">
                    Timeframe
                  </label>
                  <Tabs value={simpleTimeframe} onValueChange={setSimpleTimeframe} className="w-full">
                    <TabsList className="grid grid-cols-3 w-full">
                      <TabsTrigger value="monthly" data-testid="timeframe-monthly">Monthly</TabsTrigger>
                      <TabsTrigger value="quarterly" data-testid="timeframe-quarterly">Quarterly</TabsTrigger>
                      <TabsTrigger value="annually" data-testid="timeframe-annually">Annually</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              </TabsContent>

              {/* Advanced Mode */}
              <TabsContent value="advanced" className="space-y-5 mt-4">
                {/* Basic Plan Referrals */}
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex justify-between mb-2">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Basic Plan</label>
                      <p className="text-xs text-slate-500">${PLAN_PRICES.basic.price}/month</p>
                    </div>
                    <span className="text-sm font-bold text-purple-600">{basicReferrals}</span>
                  </div>
                  <Slider
                    value={[basicReferrals]}
                    onValueChange={(val) => setBasicReferrals(val[0])}
                    min={0}
                    max={50}
                    step={1}
                    className="w-full"
                    data-testid="basic-referrals-slider"
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>0</span>
                    <span>50</span>
                  </div>
                </div>

                {/* Premium Plan Referrals */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex justify-between mb-2">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Premium Plan</label>
                      <p className="text-xs text-slate-500">${PLAN_PRICES.premium.price}/month</p>
                    </div>
                    <span className="text-sm font-bold text-blue-600">{premiumReferrals}</span>
                  </div>
                  <Slider
                    value={[premiumReferrals]}
                    onValueChange={(val) => setPremiumReferrals(val[0])}
                    min={0}
                    max={50}
                    step={1}
                    className="w-full"
                    data-testid="premium-referrals-slider"
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>0</span>
                    <span>50</span>
                  </div>
                </div>

                {/* Brokerage Plan Referrals */}
                <div className="p-4 bg-purple-50 rounded-lg">
                  <div className="flex justify-between mb-2">
                    <div>
                      <label className="text-sm font-medium text-slate-700">Brokerage Plan</label>
                      <p className="text-xs text-slate-500">${PLAN_PRICES.brokerage.price}/month</p>
                    </div>
                    <span className="text-sm font-bold text-purple-600">{brokerageReferrals}</span>
                  </div>
                  <Slider
                    value={[brokerageReferrals]}
                    onValueChange={(val) => setBrokerageReferrals(val[0])}
                    min={0}
                    max={20}
                    step={1}
                    className="w-full"
                    data-testid="brokerage-referrals-slider"
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>0</span>
                    <span>20</span>
                  </div>
                </div>

                {/* Total Referrals Summary */}
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg">
                  <span className="text-sm font-medium text-slate-700">Total Referrals</span>
                  <Badge className="bg-purple-600 text-white text-base px-3 py-1">
                    {advancedCalculation.totalReferrals}
                  </Badge>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Calculator Results */}
        <Card className="bg-white border-slate-100 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
              Your Earnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            {calculatorMode === "simple" ? (
              /* Simple Mode Results */
              <div className="space-y-6">
                {/* Main Earnings Display */}
                <div className="text-center py-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl">
                  <p className="text-sm text-slate-500 mb-1">
                    Your {simpleTimeframe} earnings
                  </p>
                  <p className="text-4xl font-bold gradient-text" data-testid="simple-earnings-display">
                    ${simpleCalculation.monthly_earnings.toLocaleString()}
                  </p>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <Badge className={`${getTierColorClass(simpleCalculation.tier)} capitalize`}>
                      {simpleCalculation.tier}
                    </Badge>
                    <span className="text-sm text-slate-600">
                      {simpleCalculation.commission_rate}% commission
                    </span>
                  </div>
                </div>

                {/* Breakdown */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Gross Revenue</p>
                    <p className="text-lg font-bold text-slate-900">
                      ${simpleCalculation.gross_revenue.toLocaleString()}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Your Commission</p>
                    <p className="text-lg font-bold text-emerald-600">
                      ${simpleCalculation.your_commission.toLocaleString()}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Annual Projection</p>
                    <p className="text-lg font-bold text-slate-900">
                      ${simpleCalculation.annual_projection.toLocaleString()}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">2-Year LTV</p>
                    <p className="text-lg font-bold text-slate-900">
                      ${simpleCalculation.two_year_ltv.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              /* Advanced Mode Results */
              <div className="space-y-5">
                {/* Main Earnings Display */}
                <div className="text-center py-5 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl">
                  <p className="text-sm text-slate-500 mb-1">Monthly Recurring Commission</p>
                  <p className="text-4xl font-bold gradient-text" data-testid="advanced-earnings-display">
                    ${advancedCalculation.totalMonthlyCommission.toLocaleString()}
                  </p>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <Badge className={`${getTierColorClass(advancedCalculation.tier.id)} capitalize`}>
                      {advancedCalculation.tier.name}
                    </Badge>
                    <span className="text-sm text-slate-600">
                      {advancedCalculation.commissionRate}% commission
                    </span>
                  </div>
                </div>

                {/* Per-Plan Breakdown */}
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-3">Breakdown by Plan</p>
                  <div className="space-y-2">
                    {Object.entries(advancedCalculation.breakdown).map(([plan, data]) => (
                      <div key={plan} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="capitalize">{plan}</Badge>
                          <span className="text-sm text-slate-500">
                            {data.referrals} × ${PLAN_PRICES[plan].price}
                          </span>
                        </div>
                        <span className="font-bold text-emerald-600">
                          ${data.commission.toLocaleString()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Annual Projection */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Monthly Revenue</p>
                    <p className="text-lg font-bold text-slate-900">
                      ${advancedCalculation.totalMonthlyRevenue.toLocaleString()}
                    </p>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500">Annual Projection</p>
                    <p className="text-lg font-bold text-slate-900">
                      ${advancedCalculation.annualProjection.toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Next Tier Upgrade Impact */}
                {advancedCalculation.nextTier && (
                  <div className="p-4 bg-amber-50 border border-amber-100 rounded-xl">
                    <div className="flex items-start gap-3">
                      <Zap className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-900">
                          Upgrade to {advancedCalculation.nextTier.name} Tier
                        </p>
                        <p className="text-xs text-amber-700 mt-1">
                          At {advancedCalculation.nextTier.minReferrals} referrals, you&apos;d earn{" "}
                          <span className="font-bold">${advancedCalculation.nextTierCommission}/mo</span>
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-amber-600">Incremental gain:</span>
                          <Badge className="bg-amber-600 text-white">
                            +${advancedCalculation.incrementalCommission}/mo
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tier Comparison */}
      <Card className="bg-white border-slate-100 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Award className="w-5 h-5 text-amber-600" />
            Tier Comparison
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {(calculatorMode === "simple" 
              ? simpleCalculation.tier_comparison 
              : advancedCalculation.tierComparison
            ).map((tier, idx) => {
              const currentTier = calculatorMode === "simple" 
                ? simpleCalculation.tier 
                : advancedCalculation.tier.id;
              const isCurrentTier = tier.tier === currentTier;
              
              return (
                <div 
                  key={idx}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    isCurrentTier 
                      ? "border-purple-500 bg-purple-50" 
                      : "border-slate-100 bg-white"
                  }`}
                  data-testid={`tier-comparison-${tier.tier}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <Badge className={`${getTierColorClass(tier.tier)} capitalize`}>
                      {tier.tier}
                    </Badge>
                    {isCurrentTier && (
                      <Check className="w-4 h-4 text-purple-600" />
                    )}
                  </div>
                  <p className="text-sm text-slate-500">Commission Rate</p>
                  <p className="text-xl font-bold text-slate-900">{tier.commission_rate}%</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {formatReferralRange(TIERS.find((t) => t.id === tier.tier))} referrals
                  </p>
                  <p className="text-sm text-slate-500 mt-2">Monthly Earnings</p>
                  <p className="text-lg font-bold text-emerald-600">
                    ${tier.monthly_earnings.toLocaleString()}
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Payment History */}
      <Card className="bg-white border-slate-100 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Payment History
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date Paid</TableHead>
                {/* TODO: Enable when backend returns payment.referrals_included */}
                <TableHead>Referrals Included</TableHead>
                {/* TODO: Enable when backend returns payment.receipt_url */}
                <TableHead>Receipt</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                    No payment history yet
                  </TableCell>
                </TableRow>
              ) : (
                payments.map((payment, idx) => (
                  <TableRow key={idx} data-testid={`payment-row-${idx}`}>
                    <TableCell className="font-medium text-slate-900">
                      {formatMonth(payment.commission_month)}
                    </TableCell>
                    <TableCell className="font-bold text-emerald-600">
                      ${payment.amount.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        payment.payment_status === "paid" 
                          ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                          : "bg-amber-50 text-amber-700 border border-amber-200"
                      }>
                        {payment.payment_status === "paid" && <Check className="w-3 h-3 mr-1" />}
                        {payment.payment_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {payment.payment_date 
                        ? new Date(payment.payment_date).toLocaleDateString()
                        : "-"
                      }
                    </TableCell>
                    {/* TODO: Display payment.referrals_included when available from backend */}
                    <TableCell className="text-slate-500">
                      {payment.referrals_included ?? "-"}
                    </TableCell>
                    {/* TODO: Display download link when payment.receipt_url is available from backend */}
                    <TableCell>
                      {payment.receipt_url ? (
                        <a 
                          href={payment.receipt_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-purple-600 hover:text-purple-700 text-sm underline"
                        >
                          Download
                        </a>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Payments;
